import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash-es';
import classnames from 'classnames';
import { CmsEntities } from 'client/data/models/cms';
import { TrackingHandler } from 'client/tracking/handler';
import { EditorialMap } from 'client/tracking/maps/editorial';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { Share } from 'site-modules/shared/components/share/share';
import { GT_8_CLASS, LT_8_CLASS, TOP_RATED_BREADCRUMBS } from 'site-modules/shared/constants/top-rated/top-rated';
import { AwardsAccolades as TopRatedAwardsAccoladesUI } from 'site-modules/editorial/components/top-rated/awards-accolades/awards-accolades';
import { topRatedHelpers } from 'site-modules/shared/utils/top-rated/top-rated-helpers';
import { WinnerTilesNav } from 'site-modules/editorial/components/top-rated/winner-tiles-nav/2026/winner-tiles-nav';
import { CategoryTopNav } from 'site-modules/editorial/components/top-rated/category-top-nav/2026/category-top-nav';
import { ByTheNumbers } from 'site-modules/editorial/components/top-rated/by-the-numbers/2026/by-the-numbers';
import { OtherYears } from 'site-modules/editorial/components/top-rated/other-years/other-years';
import { HeroContent } from 'site-modules/editorial/components/hero-content/2026/hero-content';
import { rendererTopRatedIndex } from 'site-modules/editorial/pages/top-rated/top-rated-renderer';
import { RevealOnIntersect } from 'site-modules/editorial/components/top-rated/reveal-on-intersect/reveal-on-intersect';
import './styles/top-rated.scss';

export function TopRatedUI({ content = null, pathname = null, isMobile = false, year }) {
  useEffect(() => {
    TrackingHandler.useMap({ ...EditorialMap });
  }, []);

  const { title: contentTitle } = content;
  const mainEntries = content.child('main').children();
  const permissions = content.child('permissions');
  const canonical = getCanonical(content);
  const marketingButtonEntry = content.child('marketingButton');
  const mainEntryData = mainEntries.reduce((acc, cur) => {
    if (cur.id === 'highlights') {
      acc.highlights = cur;
    }
    if (cur.id === 'by-the-numbers') {
      acc.byTheNumbers = cur;
    }
    if (cur.id === 'winner-tiles') {
      acc.winnerTiles = cur;
    }
    if (cur.id === 'config') {
      acc.config = cur;
    }
    if (cur.id === 'featured-video-images') {
      const imageLinks = cur.links();
      const videoId = cur.metadata('videoId').value();
      const videoTitle = cur.metadata('videoTitle').value();
      const href = cur.metadata('videoThumbnailURL').value();
      const videoThumbOnly = cur.metadata('videoThumbnailOnly').value() === 'true';
      const videoAspectRatio = cur.metadata('videoThumbnailAspectRatio').value();
      acc.featuredVideoImages = [
        { videoId, videoTitle, href, title: videoTitle, videoThumbOnly, videoAspectRatio },
        ...imageLinks,
      ];
    }
    return acc;
  }, {});
  const byTheNumbersData = mainEntryData.byTheNumbers;
  const winnerTilesData = mainEntryData.winnerTiles;
  const configData = mainEntryData.config;
  const fullImageSize = mainEntryData.config && mainEntryData.config.child('fullImageSize');
  const platform = isMobile ? 'mobile' : 'wired';
  const fullImageSizeFromMetadata = fullImageSize && fullImageSize.metadata(platform).value();
  const featuredVideoImagesData = mainEntryData.featuredVideoImages;
  const featuredVideoImageGallery =
    featuredVideoImagesData &&
    featuredVideoImagesData.map(media => ({
      videoId: media.videoId,
      videoTitle: media.videoTitle,
      href: media.href,
      title: media.captionTranscript || media.videoTitle,
      videoThumbOnly: media.videoThumbOnly,
      videoAspectRatio: media.videoAspectRatio,
    }));
  const pagePaths2023 = topRatedHelpers.getPagePathFromConfigData(configData);
  const rawCategoryTilesEntries = content.child('category-tiles').children();
  const categoryTilesEntries = topRatedHelpers.mapWinners2023(rawCategoryTilesEntries, pagePaths2023);
  const tileNumClass = !isEmpty(categoryTilesEntries) && categoryTilesEntries.length <= 8 ? LT_8_CLASS : GT_8_CLASS;
  const otherYearLinks = content.child('previousYears').links();
  // hero content
  const heroContentEntry = content.child('hero-content');
  const title = heroContentEntry.title || contentTitle;

  const breadcrumbsData = TOP_RATED_BREADCRUMBS.concat({
    title,
    href: pathname,
  });

  return (
    <main
      id="main-content"
      tabIndex={-1}
      role="main"
      className={classnames('top-rated landing-page mx-auto pos-r', tileNumClass)}
    >
      <Breadcrumbs data={breadcrumbsData} className="px-0 mb-1_5 mb-md-1" />
      <HeroContent content={heroContentEntry} isMobile={isMobile} contentTitle={contentTitle} />
      <section>
        <RevealOnIntersect threshold={0} transform="none">
          <CategoryTopNav
            categoryTilesEntries={categoryTilesEntries}
            tileNumClass={tileNumClass}
            year={year}
            isMobile={isMobile}
          />
        </RevealOnIntersect>
        <article className="highlights-and-winners pos-r px-0_75 pb-1_5">
          <RevealOnIntersect>
            {byTheNumbersData && (
              <ByTheNumbers
                entry={byTheNumbersData}
                featuredVideoImageGallery={featuredVideoImageGallery}
                fullImageSizeFromMetadata={fullImageSizeFromMetadata}
                marketingButtonEntry={marketingButtonEntry}
              />
            )}
          </RevealOnIntersect>
          {winnerTilesData && (
            <WinnerTilesNav year={year} entry={winnerTilesData} isMobile={isMobile} pagePaths={pagePaths2023} />
          )}
        </article>
        <RevealOnIntersect>
          {otherYearLinks.length ? <OtherYears otherYearLinks={otherYearLinks} year={year} /> : null}
          <Share
            url={canonical}
            title={title}
            labelClassName="d-none"
            containerClassName="d-block text-center py-3"
            iconClassName="heading-4 mx-1 p-0 text-white"
          />
          {permissions && (
            <TopRatedAwardsAccoladesUI
              title={permissions.metadata('title').value()}
              content={permissions.metadata('content').value()}
              mailTo={permissions.metadata('mailTo').value()}
              email={permissions.metadata('email').value()}
              anchorClassName="text-white"
            />
          )}
        </RevealOnIntersect>
      </section>
    </main>
  );
}

TopRatedUI.propTypes = {
  content: CmsEntities.Content,
  pathname: PropTypes.string,
  isMobile: PropTypes.bool,
  year: PropTypes.string.isRequired,
};

export const TopRated = rendererTopRatedIndex(TopRatedUI);
